import React, { useState, useEffect } from 'react';
import { addMonths, getDaysInMonth, getDay } from 'date-fns';
import { Loader, Search } from 'lucide-react';
import DayCell from './DayCell';
import DetailPanel from './DetailPanel';
import CalendarHeader from './CalendarHeader';
import { isTruckScheduledForDate } from '../utils/dateUtils';
import { useTimezone } from '../../../../../hooks/useTimezone';

// Substring + ordered-character fuzzy match. Matches the helper used in OutboundView/HistoryView.
const fuzzyMatch = (text, term) => {
  if (!text || !term) return false;
  const t = String(text).toLowerCase();
  const s = term.toLowerCase();
  if (t.includes(s)) return true;
  let si = 0;
  for (let i = 0; i < t.length && si < s.length; i++) {
    if (t[i] === s[si]) si++;
  }
  return si === s.length;
};

// Load materials whose `size` is a count of pallets. `size` is stored as "number of
// load material", so materials measured in other units (Small Parcel especially, where
// size is a package count) would inflate a pallet total and are excluded. Rows with no
// load_material set predate the field and are pallets in practice.
const PALLET_MATERIALS = ['Pallet', 'Oversized Pallet'];
const isPalletLoad = (truck) => !truck.load_material || PALLET_MATERIALS.includes(truck.load_material);

// Per-mode search field whitelists. Mirrors the server's `search` filter per mode.
const SEARCHABLE_FIELDS = {
  outbound: ['carrier', 'customer', 'po', 'order_number', 'destination', 'conf_number', 'scheduler_notes', 'shipper_notes'],
  inbound: ['carrier', 'po', 'conf_number', 'vendor', 'scheduler_notes', 'receiver_notes'],
};

/**
 * Main calendar component for truck scheduling
 */
const ShippingCalendar = ({ 
  truckData, 
  setShowScheduleForm, 
  setPreSelectedDate, 
  refreshData, 
  isLoading,
  openTruckEditor,
  mode = 'inbound'
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get user's timezone for proper date handling
  const userTimezone = useTimezone();

  // Apply the search filter once before passing into the calendar grid + detail panel,
  // so day-cell counts and the selected-day list both reflect the same filtered set.
  const filteredTruckData = searchTerm
    ? truckData.filter((t) => {
        const fields = SEARCHABLE_FIELDS[mode] || SEARCHABLE_FIELDS.outbound;
        return fields.some((f) => t[f] && fuzzyMatch(t[f], searchTerm));
      })
    : truckData;
  
  // Generate calendar
  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(new Date(year, month));
    const firstDayOfMonth = getDay(new Date(year, month, 1));
    
    const calendar = [];
    let day = 1;
    
    // Create weeks
    for (let i = 0; i < 6; i++) {
      const week = [];
      
      // Fill in days of the week
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayOfMonth) || day > daysInMonth) {
          week.push(null);
        } else {
          const date = new Date(year, month, day);
          
          // Filter trucks scheduled for this day, respecting user's timezone.
          // Uses filteredTruckData so an active search narrows day-cell counts too.
          const trucksForDay = filteredTruckData.filter(truck =>
            isTruckScheduledForDate(truck, date, userTimezone)
          );
          
          // Count trucks by status
          const statusBreakdown = {
            scheduled: 0,
            inProgress: 0,
            shipped: 0,
            scheduledWithConfirmation: []
          };
          
          // Update status counts based on mode
          if (mode === 'inbound') {
            const scheduledTrucks = trucksForDay.filter(t => t.status === 'Scheduled');
            statusBreakdown.scheduled = scheduledTrucks.length;
            statusBreakdown.scheduledWithConfirmation = scheduledTrucks
              .filter(t => t.conf_number)
              .map(t => t.conf_number);

            statusBreakdown.inProgress = trucksForDay.filter(t =>
              t.status === 'Arrived' || t.status === 'Received' || t.status === 'Putaway'
            ).length;

            statusBreakdown.shipped = trucksForDay.filter(t =>
              t.status === 'Completed'
            ).length;
          } else {
            const scheduledTrucks = trucksForDay.filter(t => t.status === 'Scheduled');
            statusBreakdown.scheduled = scheduledTrucks.length;
            statusBreakdown.scheduledWithConfirmation = scheduledTrucks
              .filter(t => t.conf_number)
              .map(t => t.conf_number);

            statusBreakdown.inProgress = trucksForDay.filter(t =>
              t.status === 'Staged' || t.status === 'Loaded'
            ).length;

            statusBreakdown.shipped = trucksForDay.filter(t =>
              t.status === 'Shipped' || t.status === 'Completed'
            ).length;
          }

          // Total pallets scheduled for the day (both modes). Non-pallet materials are
          // deliberately left out of this total rather than mislabelled as pallets;
          // they can get their own per-material lines later if needed.
          let totalPallets = 0;
          trucksForDay.forEach(truck => {
            if (truck.size && truck.status !== 'Cancelled' && isPalletLoad(truck)) {
              totalPallets += Number(truck.size) || 0;
            }
          });

          week.push({
            day,
            date,
            trucksCount: trucksForDay.length,
            statusBreakdown,
            totalPallets
          });
          
          day++;
        }
      }
      
      calendar.push(week);
      
      // Break if we've processed all days in the month
      if (day > daysInMonth) {
        break;
      }
    }
    
    return calendar;
  };
  
  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(addMonths(currentMonth, -1));
    setSelectedDate(null);
    setShowDetailPanel(false);
  };
  
  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDate(null);
    setShowDetailPanel(false);
  };
  
  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowDetailPanel(true);
  };
  
  // Close detail panel
  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedDate(null);
  };
  
  // Effect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && selectedDate) {
        setShowDetailPanel(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedDate]);
  
  // Generate calendar data
  const calendarData = generateCalendar();
  
  // When no data is loaded yet
  if (isLoading && truckData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center">
          <Loader className={`w-12 h-12 text-${mode === 'inbound' ? 'blue' : 'green'}-400 animate-spin mb-3`} />
          <div className="text-slate-400">Loading calendar data...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex">
      {/* Main calendar grid */}
      <div className={`w-full ${showDetailPanel ? 'hidden lg:block lg:w-3/4' : ''} h-full p-4 overflow-auto`}>
        {/* Calendar header with navigation */}
        <CalendarHeader
          currentMonth={currentMonth}
          prevMonth={prevMonth}
          nextMonth={nextMonth}
          mode={mode}
        />

        {/* Search bar — narrows day-cell counts and the selected-day list at once. */}
        <div className="mb-3 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={mode === 'inbound'
              ? 'Search carrier, vendor, PO, conf #, notes…'
              : 'Search carrier, customer, PO, order #, conf #, destination, notes…'}
            className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          {searchTerm && (
            <div className="text-xs text-slate-500 mt-1">
              Showing only days with matching trucks. {filteredTruckData.length} of {truckData.length} trucks match.
            </div>
          )}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarData.map((week, i) => 
            week.map((day, j) => (
              <DayCell
                key={`${i}-${j}`}
                dayInfo={day}
                currentMonth={currentMonth}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                mode={mode}
              />
            ))
          )}
        </div>
      </div>
      
      {/* Detail panel */}
      {showDetailPanel && selectedDate && (
        <DetailPanel
          selectedDate={selectedDate}
          truckData={filteredTruckData}
          onClose={handleCloseDetailPanel}
          setShowScheduleForm={setShowScheduleForm}
          setPreSelectedDate={setPreSelectedDate}
          openTruckEditor={openTruckEditor}
          mode={mode}
        />
      )}
    </div>
  );
};

export default ShippingCalendar;